import type { CSSProperties } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import { hexToRgb, normalizeCssNamedColor, normalizeHexColor } from "@runcheetah/shared";
import { getExpectedBrandVisualUniverseImageItems } from "@runcheetah/shared/brand-profile";

type RouterOutputs = inferRouterOutputs<AppRouter>;

export type BrandVisualUniverseOutput = RouterOutputs["brand"]["getVisualUniverse"];
export type BrandVisualUniverse = NonNullable<BrandVisualUniverseOutput["visualUniverse"]>;
export type BrandVisualUniverseImageAsset = BrandVisualUniverseOutput["images"][number];
export type BrandVisualUniverseImageSection = BrandVisualUniverseImageAsset["section"];

export type BrandUniverseThemeStyle = CSSProperties & {
  "--universe-accent": string;
  "--universe-secondary": string;
  "--universe-tertiary": string;
  "--universe-ink": string;
  "--universe-on-accent": string;
};

export const DEFAULT_UNIVERSE_COLORS = ["#C8FF00", "#132231", "#F8FAFC"] as const;

const HASHED_HEX_COLOR_TOKEN_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const CSS_NAMED_COLOR_TOKEN_PATTERN = /^[a-z]+$/i;

export const normalizeBrandUniverseColorToken = (
  color: string | null | undefined,
): string | null => {
  const token = color?.trim();
  if (!token) {
    return null;
  }

  if (HASHED_HEX_COLOR_TOKEN_PATTERN.test(token)) {
    return normalizeHexColor(token);
  }

  return CSS_NAMED_COLOR_TOKEN_PATTERN.test(token) && normalizeCssNamedColor(token)
    ? token.toLowerCase()
    : null;
};

const getBrandUniverseColorHex = (color: string): string | null => {
  const token = normalizeBrandUniverseColorToken(color);
  if (!token) {
    return null;
  }

  return token.startsWith("#") ? token : normalizeCssNamedColor(token);
};

const toLinearColorChannel = (channel: number): number => {
  const normalized = channel / 255;
  return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
};

export const getReadableBrandUniverseTextColor = (color: string): string => {
  const hex = getBrandUniverseColorHex(color);
  const rgb = hex ? hexToRgb(hex) : null;
  if (!rgb) {
    return DEFAULT_UNIVERSE_COLORS[1];
  }

  const luminance =
    0.2126 * toLinearColorChannel(rgb.r) +
    0.7152 * toLinearColorChannel(rgb.g) +
    0.0722 * toLinearColorChannel(rgb.b);

  return luminance > 0.46 ? DEFAULT_UNIVERSE_COLORS[1] : "#FFFFFF";
};

export const getBrandUniverseThemeStyle = (colors: readonly string[]): BrandUniverseThemeStyle => {
  const safeColors = colors
    .map(normalizeBrandUniverseColorToken)
    .filter((color): color is string => Boolean(color));
  const accent = safeColors[0] ?? DEFAULT_UNIVERSE_COLORS[0];

  return {
    "--universe-accent": accent,
    "--universe-secondary": safeColors[1] ?? accent,
    "--universe-tertiary": safeColors[2] ?? DEFAULT_UNIVERSE_COLORS[2],
    "--universe-ink":
      safeColors.find((color) => getReadableBrandUniverseTextColor(color) === "#FFFFFF") ??
      DEFAULT_UNIVERSE_COLORS[1],
    "--universe-on-accent": getReadableBrandUniverseTextColor(accent),
  };
};

export const getBrandUniverseColorSwatchStyle = (color: string): CSSProperties | undefined => {
  const token = normalizeBrandUniverseColorToken(color);
  return token ? { backgroundColor: token } : undefined;
};

export const brandVisualUniverseImageKey = (input: {
  section: BrandVisualUniverseImageSection;
  itemName: string;
  itemIndex: number;
}): string => `${input.section}\0${input.itemIndex}\0${input.itemName}`;

export const buildBrandVisualUniverseImageLookup = (
  images: readonly BrandVisualUniverseImageAsset[],
): Map<string, BrandVisualUniverseImageAsset> => {
  const lookup = new Map<string, BrandVisualUniverseImageAsset>();
  for (const image of images) {
    const key = brandVisualUniverseImageKey(image);
    if (!lookup.has(key)) {
      lookup.set(key, image);
    }
  }
  return lookup;
};

export const getBrandVisualUniverseImage = (
  lookup: ReadonlyMap<string, BrandVisualUniverseImageAsset>,
  input: {
    section: BrandVisualUniverseImageSection;
    itemName: string;
    itemIndex: number;
  },
): BrandVisualUniverseImageAsset | null => lookup.get(brandVisualUniverseImageKey(input)) ?? null;

export const countExpectedBrandVisualUniverseImages = (
  visualUniverse: BrandVisualUniverse,
): number => getExpectedBrandVisualUniverseImageItems(visualUniverse).length;
